import { logEvent } from "@/App/service/analyticsService";
import { Shelf } from "capitalrx-components-2.0/lib/layout";
import { Underlined } from "capitalrx-components-2.0/lib/typography";
import { Dispatch, FC, SetStateAction, useMemo } from "react";
import styled from "styled-components";
import { ThemedButton } from "../themed";

export interface PagerProps {
  readonly pageNumber: number;
  readonly resultCount: string;
  readonly resultsPerPage: number;
  readonly setPageNumber: Dispatch<SetStateAction<number>>;
  readonly disabled?: boolean;
  readonly analyticsTag?: string;
}

const PageNumber = styled.div`
  &:hover {
    cursor: pointer;
  }
`;

/**
 * TODO -- replace this stupid component and its usages
 * with the data source and whatnot from common components
 *
 * I didn't do that here because 1. I am lazy and 2. the api's are a little wonky
 */
export const Pager: FC<PagerProps> = ({ pageNumber, resultCount, resultsPerPage, setPageNumber, disabled, analyticsTag }) => {
  const totalResults = useMemo(() => {
    if (Number.isNaN(+resultCount)) {
      return 1000;
    } else {
      return +resultCount;
    }
  }, [resultCount]);

  const onGoToMinPage = () => {
    if (analyticsTag) {
      logEvent(analyticsTag);
    }
    setPageNumber(1);
  };

  const onGoToMaxPage = () => {
    if (analyticsTag) {
      logEvent(analyticsTag);
    }
    const roundedPage = Math.ceil(totalResults / resultsPerPage);
    setPageNumber(roundedPage);
  };

  const onGoToNextPage = () => {
    if (analyticsTag) {
      logEvent(analyticsTag);
    }
    setPageNumber((prev) => prev + 1);
  };

  const onGoBackPage = () => {
    if (analyticsTag) {
      logEvent(analyticsTag);
    }
    setPageNumber((prev) => prev - 1);
  };

  const isGoToStartDisabled = pageNumber === 1 || disabled;
  const isPreviousPageDisabled = pageNumber === 1 || disabled;
  const isNextPageDisabled = pageNumber * resultsPerPage >= totalResults || disabled;
  const isGoToLastPageDisabled = pageNumber * resultsPerPage >= totalResults || disabled;

  return (
    <Shelf>
      <ThemedButton
        testId="pager-go-to-min-page"
        onClick={onGoToMinPage}
        appearance={isGoToStartDisabled ? "secondary" : "primary"}
        icon="chevron-start"
        disabled={isGoToStartDisabled}
      />
      <ThemedButton
        testId="pager-go-to-previous-page"
        onClick={() => setPageNumber((prev) => prev - 1)}
        appearance={isPreviousPageDisabled ? "secondary" : "primary"}
        icon="chevron-left"
        disabled={isPreviousPageDisabled}
      />

      {pageNumber > 1 && (
        <PageNumber data-testid="div-pager-previous-page-number" onClick={onGoBackPage}>{`${pageNumber - 1}`}</PageNumber>
      )}
      <Underlined testId="pager-current-page-number">{pageNumber}</Underlined>
      {(pageNumber + 1) * resultsPerPage <= totalResults && (
        <PageNumber data-testid="div-pager-next-page-number" onClick={() => setPageNumber(pageNumber + 1)}>
          {`${pageNumber + 1}`}
        </PageNumber>
      )}

      <ThemedButton
        testId="pager-go-to-next-page"
        onClick={onGoToNextPage}
        appearance={isNextPageDisabled ? "secondary" : "primary"}
        icon="chevron-right"
        disabled={isNextPageDisabled}
      />
      <ThemedButton
        testId="pager-go-to-max-page"
        onClick={onGoToMaxPage}
        appearance={isGoToLastPageDisabled ? "secondary" : "primary"}
        icon="chevron-end"
        disabled={isGoToLastPageDisabled}
      />
    </Shelf>
  );
};
